// ###### IMPORTS
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, FunnelIcon } from '@heroicons/react/24/solid';
import DropdownCustomDark from '../../../moduleComponents/DropdownCustomDark';
import DropdownTableStartLevelChip from './DropdownTableStartLevelChip';
import AssetsHierarchyBody from '../Common/AssetsHierarchyBody';
import { useAssetsSnapshotTable } from '../../viewServices/use_assets_snapshot_table';
import { TABLE_START_LEVEL } from '../../viewServices/assets_hierarchy';
import { formatDate as formatDateConv } from 'shared_items/formatConventions';

export {
  buildHierarchy,
  sortedLevel1Keys,
  buildLevel2Grouping,
  sortedLevel2KeysFromGrouping,
  sortedAssetsFromHierarchy,
} from '../../viewServices/assets_hierarchy';

// ###### CARD COMPONENT

function sameIds(left, right) {
  if (left.length !== right.length) return false;
  const ids = new Set(left);
  return right.every((id) => ids.has(id));
}

const optionClass = 'cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-600 text-white flex items-center';

function LocationMultiFilter({ names, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [draft, setDraft] = useState(selected);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const allChecked = names.length > 0 && draft.length === names.length;
  const label = selected.length === names.length
    ? 'All locations'
    : selected.join(', ') || 'Locations';

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (buttonRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const toggleOpen = () => {
    setOpen((value) => {
      if (!value) setDraft(selected);
      return !value;
    });
  };

  const toggle = (name) => {
    setDraft((current) => (
      current.includes(name) ? current.filter((item) => item !== name) : [...current, name]
    ));
  };

  const apply = () => {
    if (!sameIds(draft, selected)) onChange(draft);
    setOpen(false);
  };

  const menuStyle = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return {};
    return {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      zIndex: 8,
      minWidth: rect.width,
    };
  };

  const row = (checked, text, onClick, textClass = '') => (
    <div
      onClick={onClick}
      className={`${optionClass} ${checked ? 'text-green-400' : ''}`}
    >
      <input
        type="checkbox"
        checked={checked}
        readOnly
        className="mr-2 accent-green-400 pointer-events-none"
      />
      <span className={`min-w-0 truncate ${textClass}`}>{text}</span>
    </div>
  );

  return (
    <div className="relative min-w-[140px] max-w-[180px]" ref={buttonRef}>
      <button
        type="button"
        onClick={toggleOpen}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        title={label}
        style={{ backgroundColor: `rgb(107 114 128 / ${isHover ? 1 : 0.3})` }}
        className="inline-flex w-full cursor-pointer items-center justify-between gap-1 rounded-md border-0 px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-white focus:ring-opacity-30"
      >
        <span className="min-w-0 truncate text-left">{label}</span>
        <ChevronDownIcon className={`h-4 w-4 flex-shrink-0 text-white opacity-70 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={menuStyle()}
          className="overflow-hidden rounded-md bg-gray-700 shadow-lg ring-1 ring-black ring-opacity-5"
        >
          <div className="max-h-48 overflow-y-auto custom-scrollbar-dark">
            {row(allChecked, 'Select all', () => setDraft(allChecked ? [] : names), 'font-semibold')}
            {names.map((name) => row(draft.includes(name), name, () => toggle(name)))}
            {names.length === 0 && (
              <div className="px-3 py-1.5 text-xs text-gray-300 italic">No locations</div>
            )}
          </div>
          <div className="flex border-t border-gray-600 px-2 py-1.5">
            <button
              type="button"
              onClick={apply}
              disabled={sameIds(draft, selected)}
              className="cursor-pointer rounded-full border-0 bg-green-600 px-2.5 py-1 text-[10px] font-normal leading-none text-white hover:bg-green-700 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-green-600"
            >
              Apply
            </button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

const ASSET_TYPE_FILTER_OPTIONS = [
  { value: 'deposit', label: 'Deposit' },
  { value: 'tradeable', label: 'Tradeable' },
];

const CardAssetsDetailed = ({ onEditAsset }) => {
  const {
    loading,
    displayedAssets: filteredAssets,
    hierarchy,
    sortedLevel1,
    snapshotMap,
    snapshotDate,
    snapshotList: snapshotOptions,
    selectedSnapshotId,
    onSelectSnapshot,
    tableStartLevel,
    onTableStartLevelChange,
    hideInactive,
    setHideInactive: onToggleHideInactive,
    assetNameSearch,
    setAssetNameSearch: onAssetNameSearchChange,
    assetTypeFilter,
    setAssetTypeFilter: onAssetTypeFilterChange,
    assetLocationFilter,
    setAssetLocationFilter: onAssetLocationFilterChange,
    locationNames,
    showOnlyInSnapshot: showOnlyWithValue,
    onToggleShowOnlyWithValue,
  } = useAssetsSnapshotTable();
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isSnapshotDropdownOpen, setIsSnapshotDropdownOpen] = useState(false);
  const snapshotDropdownRef = useRef(null);
  const assetTypeDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (snapshotDropdownRef.current && !snapshotDropdownRef.current.contains(e.target)) {
        setIsSnapshotDropdownOpen(false);
      }
    };
    if (isSnapshotDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isSnapshotDropdownOpen]);

  const hasActiveFilter =
    showOnlyWithValue ||
    hideInactive ||
    (assetNameSearch?.trim() ?? '') !== '' ||
    Boolean(assetTypeFilter) ||
    Boolean(assetLocationFilter);

  return (
    <div className="relative w-full overflow-hidden rounded-xl bg-white shadow">
      {/* Header — mobile: title | chip+date+filter; sm+: 3-col grid (title | segmented | date+filter) */}
      <div className="bg-gray-600 border-b border-gray-200 rounded-t-xl">
        <div className="px-4 py-2 grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-2 sm:grid-cols-3">
          <div className="flex min-w-0 items-center justify-start">
            <div className="flex min-w-0 items-center gap-2 text-white">
              <h2 className="truncate text-lg font-semibold text-white">Assets</h2>
              {!loading && (
                <span className="shrink-0 text-xs text-white opacity-70">({filteredAssets.length})</span>
              )}
            </div>
          </div>
          <div className="flex min-w-0 items-center justify-end gap-1 sm:contents">
            {typeof onTableStartLevelChange === 'function' && (
              <div className="flex shrink-0 sm:hidden">
                <DropdownTableStartLevelChip
                  value={tableStartLevel}
                  onChange={onTableStartLevelChange}
                  className="justify-end"
                />
              </div>
            )}
            <div
              className="hidden min-w-0 items-center justify-center sm:flex"
              role="group"
              aria-label="Nível inicial da tabela"
            >
              <div className="flex rounded-full bg-gray-500 bg-opacity-30 p-0.5">
                {[
                  { level: TABLE_START_LEVEL.L1, label: 'Category', title: 'Table starting at category (L1)' },
                  { level: TABLE_START_LEVEL.L2, label: 'Sub-Category', title: 'Table starting at sub-category (L2)' },
                  { level: TABLE_START_LEVEL.L3, label: 'Asset', title: 'Assets only (L3)' },
                  { level: TABLE_START_LEVEL.LOCATION, label: 'Location', title: 'Group by location' },
                ].map(({ level, label, title }, index, options) => (
                  <button
                    key={level}
                    type="button"
                    onClick={() => onTableStartLevelChange?.(level)}
                    className={`px-2.5 py-1 text-xs font-medium transition-all ${
                      index === 0 ? 'rounded-l-full' : ''
                    } ${index === options.length - 1 ? 'rounded-r-full' : ''} ${
                      tableStartLevel === level
                        ? 'bg-green-400 text-white'
                        : 'text-white text-opacity-70 hover:bg-gray-500 hover:bg-opacity-20 hover:text-opacity-100'
                    }`}
                    title={title}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex min-w-0 shrink-0 items-center justify-end gap-2">
              <div className="relative flex items-center" ref={snapshotDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsSnapshotDropdownOpen((o) => !o)}
                  className="flex max-w-[11rem] items-center gap-1 rounded px-2 py-1 text-left text-xs text-white opacity-90 bg-gray-500 bg-opacity-30 transition-colors hover:bg-opacity-40 hover:opacity-100 sm:max-w-none"
                  title="Select snapshot"
                >
                  {snapshotDate ? (
                    <span className="min-w-0 truncate">{formatDateConv(snapshotDate, 'display')}</span>
                  ) : (
                    <span className="whitespace-nowrap">Select snapshot</span>
                  )}
                  <ChevronDownIcon className={`h-3.5 w-3.5 flex-shrink-0 transition-transform ${isSnapshotDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {isSnapshotDropdownOpen && (
                  <div className="absolute right-0 top-full z-20 mt-1 max-h-48 min-w-[140px] overflow-auto rounded-lg border border-gray-200 bg-gray-50 py-1 shadow-lg">
                    {snapshotOptions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => {
                          onSelectSnapshot?.(s.id);
                          setIsSnapshotDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 ${selectedSnapshotId === s.id ? 'bg-green-50 font-medium text-green-800' : 'text-gray-700'}`}
                      >
                        {formatDateConv(s.snapshot_date, 'display')}
                      </button>
                    ))}
                    {snapshotOptions.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">No snapshots</div>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setIsFilterExpanded((f) => !f)}
                className={`flex-shrink-0 rounded-full p-1 transition-all ${
                  hasActiveFilter
                    ? 'bg-green-400 text-white'
                    : 'bg-gray-500 bg-opacity-30 text-white text-opacity-70 hover:bg-opacity-40'
                }`}
                aria-label="Toggle filters"
              >
                <FunnelIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
        <div className={`overflow-hidden transition-all duration-200 ease-in-out ${isFilterExpanded ? 'h-10' : 'h-0'}`}>
          <div className="h-10 px-4 flex items-center bg-gray-500 bg-opacity-20 gap-4 flex-wrap">
            <input
              type="search"
              value={assetNameSearch}
              onChange={(e) => onAssetNameSearchChange?.(e.target.value)}
              placeholder="Nome do asset"
              className="h-6 px-2 rounded text-xs text-gray-800 bg-white border border-gray-300 min-w-[120px] max-w-[180px] placeholder:text-gray-400"
              aria-label="Filtrar por nome do asset"
            />
            {typeof onAssetTypeFilterChange === 'function' && (
              <div className="min-w-[130px]" ref={assetTypeDropdownRef}>
                <DropdownCustomDark
                  options={ASSET_TYPE_FILTER_OPTIONS}
                  selectedValue={assetTypeFilter || ''}
                  onSelect={onAssetTypeFilterChange}
                  placeholder="All types"
                  width="min-w-[130px]"
                  usePortal
                  portalRef={assetTypeDropdownRef}
                  showAllOption
                  allOptionText="All types"
                  allOptionValue=""
                  showRadio
                />
              </div>
            )}
            {typeof onAssetLocationFilterChange === 'function' && (
              <LocationMultiFilter
                names={locationNames || []}
                selected={assetLocationFilter == null ? (locationNames || []) : assetLocationFilter}
                onChange={onAssetLocationFilterChange}
              />
            )}
            <label className="flex items-center gap-1.5 cursor-pointer text-xs text-white opacity-80 hover:opacity-100">
              <input
                type="checkbox"
                checked={hideInactive}
                onChange={(e) => onToggleHideInactive(e.target.checked)}
                className="h-3 w-3 accent-gray-400"
              />
              Hide inactive
            </label>
            {typeof onToggleShowOnlyWithValue === 'function' && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  role="switch"
                  aria-checked={showOnlyWithValue}
                  aria-label={showOnlyWithValue ? 'Only in snapshot' : 'All assets'}
                  title={showOnlyWithValue ? 'Show only assets with value in current snapshot' : 'Show all assets'}
                  onClick={() => onToggleShowOnlyWithValue(!showOnlyWithValue)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-0 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-600 ${
                    showOnlyWithValue ? 'bg-green-500' : 'bg-gray-500 bg-opacity-50'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 shrink-0 transform rounded-full bg-white shadow ring-0 transition-transform ${
                      showOnlyWithValue ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'
                    }`}
                    aria-hidden
                  />
                </button>
                <span className="text-xs text-white opacity-90 select-none">
                  {showOnlyWithValue ? 'only in snapshot' : 'all assets'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <AssetsHierarchyBody
        loading={loading}
        filteredAssets={filteredAssets}
        hierarchy={hierarchy}
        sortedLevel1={sortedLevel1}
        snapshotMap={snapshotMap}
        tableStartLevel={tableStartLevel}
        onEditAsset={onEditAsset}
      />
    </div>
  );
};

export default CardAssetsDetailed;
